import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ReviewFAQModal } from 'us-react-components/dist/ReviewFAQModal/ReviewFAQModal';
import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const LEVELS: { rating: number; labelKey: string }[] = [
  { rating: 5, labelKey: 'pages.sitterReview.rating.levels.5' },
  { rating: 4, labelKey: 'pages.sitterReview.rating.levels.4' },
  { rating: 3, labelKey: 'pages.sitterReview.rating.levels.3' },
  { rating: 2, labelKey: 'pages.sitterReview.rating.levels.2' },
  { rating: 1, labelKey: 'pages.sitterReview.rating.levels.1' },
];

export const Rating = observer((): JSX.Element | null => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: sitterIdParam } = useParams<{ id: string }>();
  const { stores } = useDependencies();
  const user = stores.user.user;
  const store = stores.sitterReview;

  const [saving, setSaving] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);

  useEffect(() => {
    document.title = 'UrbanSitter - Rate Caregiver';
  }, []);

  useEffect(() => {
    const sitterId = Number(sitterIdParam);
    if (!user?.id || !sitterId) return;

    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const eligible = await store.loadForSitter(sitterId, user.id);
        if (cancelled) return;
        if (!eligible) {
          navigate(`/sitter/${sitterId}/recommend`, { replace: true });
        }
      } catch {
        /* leave the form unpopulated */
      }
    };
    void load();

    return (): void => { cancelled = true; };
  }, [sitterIdParam, user?.id, navigate, store]);

  const review = store.review;
  const sitter = store.sitter ?? review?.sitter;
  const rating = review?.rating ?? 0;

  const onSubmit = async (): Promise<void> => {
    if (!review || !sitter?.id || rating < 1) return;
    setSaving(true);
    try {
      if (rating > 3) {
        const saved = await store.save();
        const id = saved?.id ?? 0;
        navigate(`/sitter/${sitter.id}/review/${id}`);
      } else {
        // Low rating: skip the save step; next page handles issues + comment,
        // then submits the review there. Matches Angular behavior.
        navigate(`/sitter/${sitter.id}/review/0`);
      }
    } catch (err) {
      console.warn('[SitterReviewRating] submit failed', err);
    } finally {
      setSaving(false);
    }
  };

  if (!review || !sitter) {
    return <div className='container py-6' />;
  }

  const levelLabel = rating > 0 ? t(LEVELS.find((l) => l.rating === rating)!.labelKey) : null;

  return (
    <div className='container py-6 max-w-2xl mx-auto'>
      <div className='bg-white rounded-lg border border-neutral-200 p-5'>
        <div className='flex items-start gap-3'>
          <a href={`/app/sitter/${sitter.id}`}>
            <img
              src={sitter.pictureUrl}
              alt={sitter.firstName}
              className='w-20 h-20 rounded object-cover'
            />
          </a>
          <h2 className='text-xl font-semibold flex-1'>
            {t('pages.sitterReview.rating.heading', { name: sitter.firstName })}
          </h2>
        </div>

        <hr className='my-4' />

        <div className='text-center'>
          <div className='flex justify-center gap-1'>
            {[0, 1, 2, 3, 4].map((i) => (
              <Button
                key={i}
                buttonType={ButtonType.tertiary}
                onClick={(): void => store.setRating(i + 1)}
                className='text-4xl'
                aria-label={t('pages.sitterReview.rating.starLabel', { count: i + 1 })}
              >
                <span className={i < rating ? 'text-amber-400' : 'text-neutral-300'}>★</span>
              </Button>
            ))}
          </div>
          {levelLabel && <div className='mt-2 font-medium'>{levelLabel}</div>}
        </div>

        <hr className='my-4' />

        <ul className='space-y-2'>
          {LEVELS.map((level) => (
            <li key={level.rating}>
              <Button
                buttonType={ButtonType.tertiary}
                className='w-full flex justify-between items-center py-2 px-2 rounded hover:bg-neutral-100 cursor-pointer'
                onClick={(): void => store.setRating(level.rating)}
              >
                <span>{t(level.labelKey)}</span>
                <span className='flex gap-0.5'>
                  {[0, 1, 2, 3, 4].map((i) => (
                    <span key={i} className={i < level.rating ? 'text-amber-400' : 'text-neutral-300'}>★</span>
                  ))}
                </span>
              </Button>
            </li>
          ))}
        </ul>

        <hr className='my-4' />

        {store.visibilityPublic ? (
          <p className='text-sm'>
            <strong>{t('pages.sitterReview.rating.publicLabel')}</strong>{' '}
            {t('pages.sitterReview.rating.publicDescription')}{' '}
            <Button buttonType={ButtonType.tertiary} className='underline' onClick={store.toggleVisibility}>
              {t('pages.sitterReview.rating.makePrivate')}
            </Button>
          </p>
        ) : (
          <p className='text-sm'>
            <strong>{t('pages.sitterReview.rating.privateLabel')}</strong>{' '}
            {t('pages.sitterReview.rating.privateDescription')}{' '}
            <Button buttonType={ButtonType.tertiary} className='underline' onClick={store.toggleVisibility}>
              {t('pages.sitterReview.rating.makePublic')}
            </Button>
          </p>
        )}

        <Button
          id='submit-star-rating'
          buttonType={ButtonType.primary}
          className='mt-4'
          disabled={rating < 1 || saving}
          onClick={onSubmit}
        >
          {rating > 0 && rating < 4
            ? t('pages.sitterReview.rating.continue')
            : t('pages.sitterReview.rating.submit')}
        </Button>

        <div className='text-center mt-3'>
          <Button
            id='review-faq'
            buttonType={ButtonType.tertiary}
            className='underline text-sm'
            onClick={(): void => setFaqOpen(true)}
          >
            Questions about reviews?
          </Button>
        </div>
      </div>

      <ReviewFAQModal isOpen={faqOpen} onClose={(): void => setFaqOpen(false)} />
    </div>
  );
});
